import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, Modal, Alert, Spinner, InputGroup, Nav } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { User } from '../models/User';

type TabType = 'clients' | 'admins';

interface AdminUser {
  rowKey: string;
  email: string;
  isAdmin: boolean;
  addedAt: string;
  lastLogin: string;
  addedBy: string;
}

const AdminUserManagement: React.FC = () => {
  const { storageService, adminService } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('clients');
  const [users, setUsers] = useState<User[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [containers, setContainers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  
  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form state for assign modal
  const [selectedContainer, setSelectedContainer] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<'read' | 'write' | 'readwrite'>('read');
  const [submitting, setSubmitting] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [accessBusy, setAccessBusy] = useState<string | null>(null); // `${userId}|${container}` while updating
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, [storageService, adminService]);

  const loadData = async () => {
    if (!storageService) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create a simple helper to call admin APIs
      const callAdminAPI = async (endpoint: string, body: any) => {
        const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
        const credentials = {
          userObjectId: sessionStorage.getItem('msalAccount') 
            ? JSON.parse(sessionStorage.getItem('msalAccount')!).localAccountId || ''
            : '',
          userEmail: sessionStorage.getItem('msalAccount')
            ? JSON.parse(sessionStorage.getItem('msalAccount')!).username || ''
            : ''
        };
        
        const response = await fetch(`${functionUrl}/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...credentials, ...body })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
        
        return response.json();
      };
      
      // Load users, admins, and containers in parallel
      const promises = [
        callAdminAPI('list_users', {}),
        storageService.getContainers()
      ];
      
      // Load admins if adminService is available
      if (adminService) {
        promises.push(adminService.listAdmins().catch(err => {
          console.error('Error loading admins:', err);
          return [];
        }));
      } else {
        promises.push(Promise.resolve([]));
      }
      
      const [usersResponse, containersData, adminsData] = await Promise.all(promises);
      
      setUsers(usersResponse.users || []);
      setContainers(containersData);
      setAdmins(adminsData || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active';
    const confirmMessage = newStatus === 'disabled' 
      ? `Are you sure you want to disable user "${user.displayName}"? They will not be able to login.`
      : `Are you sure you want to enable user "${user.displayName}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const credentials = {
        userObjectId: sessionStorage.getItem('msalAccount') 
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).localAccountId || ''
          : '',
        userEmail: sessionStorage.getItem('msalAccount')
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).username || ''
          : ''
      };
      
      const response = await fetch(`${functionUrl}/update_user_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentials,
          userId: user.userId,
          status: newStatus
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update status' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      setSuccess(`User ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      await loadData(); // Reload data
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleToggleCanCreate = async (user: User) => {
    const next = !(user as any).canCreateContainers;
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const credentials = {
        userObjectId: sessionStorage.getItem('msalAccount')
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).localAccountId || ''
          : '',
        userEmail: sessionStorage.getItem('msalAccount')
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).username || ''
          : ''
      };

      const response = await fetch(`${functionUrl}/update_user_permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentials,
          userId: user.userId,
          canCreateContainers: next
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update permission' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      setSuccess(`"Can create folders" ${next ? 'enabled' : 'disabled'} for ${user.displayName}`);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update permission');
    }
  };

  const getAdminCreds = () => ({
    userObjectId: sessionStorage.getItem('msalAccount')
      ? JSON.parse(sessionStorage.getItem('msalAccount')!).localAccountId || ''
      : '',
    userEmail: sessionStorage.getItem('msalAccount')
      ? JSON.parse(sessionStorage.getItem('msalAccount')!).username || ''
      : ''
  });

  // Change a user's permission level on a folder (assign_user_container upserts).
  const handleChangePermission = async (user: User, containerName: string, permissions: 'read' | 'write' | 'readwrite') => {
    setAccessBusy(`${user.userId}|${containerName}`);
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const response = await fetch(`${functionUrl}/assign_user_container`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...getAdminCreds(), userId: user.userId, containerName, permissions })
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({ error: 'Failed to update access' }));
        throw new Error(e.error || `HTTP ${response.status}`);
      }
      setSuccess(`Updated ${user.displayName}'s access to "${containerName}" → ${permissions}`);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update access');
    } finally {
      setAccessBusy(null);
    }
  };

  // Revoke a user's access to a folder entirely.
  const handleRevokeAccess = async (user: User, containerName: string) => {
    if (!adminService) { setError('Admin service unavailable'); return; }
    if (!window.confirm(`Remove ${user.displayName}'s access to "${containerName}"?`)) return;
    setAccessBusy(`${user.userId}|${containerName}`);
    try {
      await adminService.removeUserContainer(user.userId, containerName);
      setSuccess(`Removed ${user.displayName}'s access to "${containerName}"`);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove access');
    } finally {
      setAccessBusy(null);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Delete user "${user.displayName}" (${user.email})?\n\nThis removes their account and all folder access. This cannot be undone.`)) {
      return;
    }
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const response = await fetch(`${functionUrl}/delete_user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...getAdminCreds(), userId: user.userId })
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({ error: 'Failed to delete user' }));
        throw new Error(e.error || `HTTP ${response.status}`);
      }
      setSuccess(`Deleted ${user.displayName}`);
      if (expandedUserId === user.userId) setExpandedUserId(null);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleOpenResetModal = (user: User) => {
    setResetUser(user);
    setResetPassword('');
    setResetError(null);
    setResetDone(false);
    setShowResetModal(true);
  };

  const generatePassword = () => {
    // Readable temporary password: word-ish + digits + symbol.
    const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let pw = '';
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setResetPassword(pw + '#9');
  };

  const handleResetPassword = async () => {
    if (!resetUser || resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    setResettingPassword(true);
    setResetError(null);
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const response = await fetch(`${functionUrl}/reset_user_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...getAdminCreds(), userId: resetUser.userId, newPassword: resetPassword })
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({ error: 'Failed to reset password' }));
        throw new Error(e.error || `HTTP ${response.status}`);
      }
      setResetDone(true);
    } catch (err: any) {
      setResetError(err.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleOpenAssignModal = (user: User) => {
    setSelectedUser(user);
    setSelectedContainer('');
    setSelectedPermissions('read');
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedUser(null);
    setSelectedContainer('');
    setSelectedPermissions('read');
  };

  const handleAssignContainer = async () => {
    if (!selectedUser || !selectedContainer) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const credentials = {
        userObjectId: sessionStorage.getItem('msalAccount') 
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).localAccountId || ''
          : '',
        userEmail: sessionStorage.getItem('msalAccount')
          ? JSON.parse(sessionStorage.getItem('msalAccount')!).username || ''
          : ''
      };
      
      const response = await fetch(`${functionUrl}/assign_user_container`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentials,
          userId: selectedUser.userId,
          containerName: selectedContainer,
          permissions: selectedPermissions
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to assign container' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      setSuccess(`Container "${selectedContainer}" assigned to ${selectedUser.displayName}`);
      handleCloseAssignModal();
      await loadData(); // Reload data
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to assign container');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter admins based on search term
  const filteredAdmins = admins.filter(admin =>
    admin.email.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
    admin.rowKey.toLowerCase().includes(adminSearchTerm.toLowerCase())
  );

  const handleToggleAdminStatus = async (admin: AdminUser) => {
    const newStatus = !admin.isAdmin;
    const confirmMessage = newStatus
      ? `Are you sure you want to grant admin access to "${admin.email}"?`
      : `Are you sure you want to revoke admin access from "${admin.email}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      if (!adminService) {
        throw new Error('Admin service not available');
      }
      
      await adminService.updateAdminStatus(admin.rowKey, newStatus);
      
      setSuccess(`Admin access ${newStatus ? 'granted' : 'revoked'} successfully`);
      await loadData(); // Reload data
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update admin status');
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' 
      ? <Badge bg="success">Active</Badge>
      : <Badge bg="danger">Disabled</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading user management...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h3>
            <i className="bi bi-people me-2"></i>
            User Management
          </h3>
          <p className="text-muted">Manage user accounts and container assignments</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'clients'}
            onClick={() => setActiveTab('clients')}
            style={{ cursor: 'pointer' }}
          >
            <i className="bi bi-people me-2"></i>
            Client Users
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'admins'}
            onClick={() => setActiveTab('admins')}
            style={{ cursor: 'pointer' }}
          >
            <i className="bi bi-shield-check me-2"></i>
            Admin Users
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === 'clients' ? (
      <Card>
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className="mb-0">
                <i className="bi bi-person-lines-fill me-2"></i>
                Registered Users ({filteredUsers.length})
              </h5>
            </Col>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                    <i className="bi bi-x-lg"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-people" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">
                {searchTerm ? 'No users match your search' : 'No users registered yet'}
              </p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Containers</th>
                  <th>Can create folders</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isExpanded = expandedUserId === user.userId;
                  const userContainers = ((user as any).containers || []) as { name: string; permissions: 'read' | 'write' | 'readwrite' }[];
                  return (
                  <React.Fragment key={user.userId}>
                  <tr>
                    <td>
                      <div>
                        <strong>{user.displayName}</strong>
                        <br />
                        <small className="text-muted">{user.email}</small>
                      </div>
                    </td>
                    <td>{getStatusBadge(user.status)}</td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        onClick={() => setExpandedUserId(isExpanded ? null : user.userId)}
                        title="Manage folder access"
                      >
                        <Badge bg="info" pill>
                          {(user as any).containerCount || 0}
                        </Badge>
                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-1 small`}></i>
                      </Button>
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`can-create-${user.userId}`}
                        checked={!!(user as any).canCreateContainers}
                        onChange={() => handleToggleCanCreate(user)}
                        title="Allow this user to create new Shared Folders"
                      />
                    </td>
                    <td>
                      <small>{formatDate(user.createdAt || '')}</small>
                    </td>
                    <td>
                      <small>{formatDate(user.lastLogin || '')}</small>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenAssignModal(user)}
                          title="Assign container"
                        >
                          <i className="bi bi-folder-plus"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleOpenResetModal(user)}
                          title="Reset password"
                        >
                          <i className="bi bi-key"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user)}
                          title="Delete user"
                        >
                          <i className="bi bi-trash3"></i>
                        </Button>
                        <Button
                          variant={user.status === 'active' ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          onClick={() => handleToggleUserStatus(user)}
                          title={user.status === 'active' ? 'Disable user' : 'Enable user'}
                        >
                          <i className={`bi bi-${user.status === 'active' ? 'ban' : 'check-circle'}`}></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-light">
                        <div className="p-2">
                          <div className="fw-medium mb-2">
                            <i className="bi bi-shield-lock me-2 text-primary"></i>
                            Folder access for {user.displayName}
                            <span className="text-muted fw-normal ms-2">— tick to grant, untick to remove</span>
                          </div>
                          {containers.length === 0 ? (
                            <small className="text-muted">No folders exist yet.</small>
                          ) : (
                            <Table size="sm" borderless className="mb-0 align-middle">
                              <tbody>
                                {containers.map((folderName) => {
                                  const assigned = userContainers.find((c) => c.name === folderName);
                                  const busy = accessBusy === `${user.userId}|${folderName}`;
                                  return (
                                    <tr key={folderName}>
                                      <td style={{ width: '36px' }}>
                                        <Form.Check
                                          type="checkbox"
                                          checked={!!assigned}
                                          disabled={busy}
                                          onChange={(e) =>
                                            e.target.checked
                                              ? handleChangePermission(user, folderName, 'read')
                                              : handleRevokeAccess(user, folderName)
                                          }
                                        />
                                      </td>
                                      <td>
                                        <i className={`bi bi-folder${assigned ? '-fill' : ''} me-2 ${assigned ? 'text-primary' : 'text-muted'}`}></i>
                                        <span className={assigned ? 'fw-medium' : 'text-muted'}>{folderName}</span>
                                      </td>
                                      <td style={{ width: '190px' }}>
                                        {assigned && (
                                          <Form.Select
                                            size="sm"
                                            value={assigned.permissions}
                                            disabled={busy}
                                            onChange={(e) => handleChangePermission(user, folderName, e.target.value as 'read' | 'write' | 'readwrite')}
                                          >
                                            <option value="read">Read Only</option>
                                            <option value="write">Write Only</option>
                                            <option value="readwrite">Read &amp; Write</option>
                                          </Form.Select>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      ) : (
        <Card>
          <Card.Header className="bg-white">
            <Row className="align-items-center">
              <Col md={6}>
                <h5 className="mb-0">
                  <i className="bi bi-shield-check me-2"></i>
                  Admin Users ({filteredAdmins.length})
                </h5>
              </Col>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search admins by email..."
                    value={adminSearchTerm}
                    onChange={(e) => setAdminSearchTerm(e.target.value)}
                  />
                  {adminSearchTerm && (
                    <Button variant="outline-secondary" onClick={() => setAdminSearchTerm('')}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {filteredAdmins.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-shield-check" style={{ fontSize: '4rem', color: '#ccc' }}></i>
                <p className="text-muted mt-3">
                  {adminSearchTerm ? 'No admins match your search' : 'No admin users found'}
                </p>
              </div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Email</th>
                    <th>Admin Access</th>
                    <th>Added</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdmins.map((admin) => (
                    <tr key={admin.rowKey}>
                      <td>
                        <div>
                          <strong>{admin.email}</strong>
                          <br />
                          <small className="text-muted">{admin.rowKey}</small>
                        </div>
                      </td>
                      <td>
                        {admin.isAdmin ? (
                          <Badge bg="success">Admin</Badge>
                        ) : (
                          <Badge bg="secondary">Pending</Badge>
                        )}
                      </td>
                      <td>
                        <small>{formatDate(admin.addedAt)}</small>
                      </td>
                      <td>
                        <small>{formatDate(admin.lastLogin)}</small>
                      </td>
                      <td>
                        <Button
                          variant={admin.isAdmin ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          onClick={() => handleToggleAdminStatus(admin)}
                          title={admin.isAdmin ? 'Revoke admin access' : 'Grant admin access'}
                        >
                          <i className={`bi bi-${admin.isAdmin ? 'shield-x' : 'shield-check'}`}></i>
                          {' '}
                          {admin.isAdmin ? 'Revoke' : 'Grant'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetUser && !resetDone && (
            <>
              <p className="mb-3">
                Set a new password for <strong>{resetUser.displayName}</strong>{' '}
                (<span className="text-muted">{resetUser.email}</span>). Share it with them securely;
                they can use it to log in immediately.
              </p>
              {resetError && <Alert variant="danger" className="py-2">{resetError}</Alert>}
              <Form.Group>
                <Form.Label>New password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="At least 8 characters"
                  />
                  <Button variant="outline-secondary" onClick={generatePassword}>
                    <i className="bi bi-shuffle me-1"></i>Generate
                  </Button>
                </InputGroup>
              </Form.Group>
            </>
          )}
          {resetUser && resetDone && (
            <Alert variant="success" className="mb-0">
              <div className="mb-2">
                <i className="bi bi-check-circle-fill me-2"></i>
                Password reset for <strong>{resetUser.displayName}</strong>.
              </div>
              <div>Give them this password:</div>
              <code className="d-block mt-1 p-2 bg-white border rounded">{resetPassword}</code>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!resetDone ? (
            <>
              <Button variant="secondary" onClick={() => setShowResetModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleResetPassword} disabled={resetPassword.length < 8 || resettingPassword}>
                {resettingPassword ? 'Resetting…' : 'Reset Password'}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => setShowResetModal(false)}>Done</Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Assign Container Modal */}
      <Modal show={showAssignModal} onHide={handleCloseAssignModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-folder-plus me-2"></i>
            Assign Container
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p className="mb-3">
                Assign a container to <strong>{selectedUser.displayName}</strong>
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label>Container</Form.Label>
                <Form.Select
                  value={selectedContainer}
                  onChange={(e) => setSelectedContainer(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select a container...</option>
                  {containers.map(container => (
                    <option key={container} value={container}>
                      {container}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose which container the user can access
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Permissions</Form.Label>
                <Form.Select
                  value={selectedPermissions}
                  onChange={(e) => setSelectedPermissions(e.target.value as any)}
                  disabled={submitting}
                >
                  <option value="read">Read Only</option>
                  <option value="write">Write Only</option>
                  <option value="readwrite">Read & Write</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Set the access level for this container
                </Form.Text>
              </Form.Group>

              <Alert variant="info" className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                <small>The user will be able to access this container immediately after assignment.</small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAssignModal} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignContainer}
            disabled={!selectedContainer || submitting}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Assigning...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Assign Container
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminUserManagement;

